// frontend/src/components/billing/billing-section.tsx
// Read DESIGN.md before modifying this file.
// Phase 1 split (spec 2026-06-18): the account & payment surface — subscription
// status + recovery (Keep Plan / Cancel Scheduled Change), native billing
// details + saved card + invoice history. Card management is fully native
// (Add Card / Set Default / Remove in billing-method.tsx); the Stripe Customer
// Portal "Manage Billing" button was retired now that natives cover it. Plan
// identity & included-messages capacity live on the Plans page (tier grid +
// usage meter), NOT duplicated here. Capacity & spend (tiers, usage, overflow,
// top-up) live in plans-section.tsx.
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { InfoAlert } from '@/components/info-alert';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useWorkspace } from '@/contexts/workspace-context';
import { isValidPublicId } from '@/lib/public-id';
import { emit } from '@/lib/analytics';
import { useSubscriptionInvalidate, useSubscriptionQuery } from './queries';
import { BillingDetails } from './billing-details';
import { BillingMethod } from './billing-method';
import { BillingHistory } from './billing-history';

export function BillingSection() {
  // Analytics-only: the plan_checkout_completed event is workspace-keyed, so
  // the emit stays best-effort on the active workspace. Rendering never
  // depends on it — the page renders purely from the URL's org (spec D5).
  const { activeWorkspace } = useWorkspace();
  const { orgId } = useParams<{ orgId: string }>();
  const validOrgId = orgId && isValidPublicId(orgId, 'org') ? orgId : null;
  const subscriptionQuery = useSubscriptionQuery(validOrgId);
  const invalidateSubscription = useSubscriptionInvalidate(validOrgId);
  const subscription = subscriptionQuery.data ?? null;
  // Any in-flight fetch shows the loading card — first load AND every refetch,
  // matching the manual load() that always flipped loading on.
  const loading = subscriptionQuery.isPending || subscriptionQuery.isFetching;
  const error = subscriptionQuery.isError;

  const hasActiveSub =
    subscription &&
    subscription.plan.slug !== 'free' &&
    ['active', 'past_due', 'trialing'].includes(subscription.status);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get('billing');
    if (!billing) return;

    const cleanUrl = () => {
      params.delete('billing');
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
    };

    if (billing === 'success') {
      toast.success('Subscription activated!');
      // Phase 125-06 -- emit plan_checkout_completed at the ?billing=success
      // moment (Stripe redirect landed on this page). The `plan` field is
      // best-effort: if `subscription` has already resolved we use its
      // `planSlug`; otherwise fall back to 'unknown' so the funnel
      // captures the redirect NOW without waiting for an async refetch.
      // workspace_id resolves from the activeWorkspace context.
      if (activeWorkspace?.id) {
        emit('plan_checkout_completed', {
          workspace_id: activeWorkspace.id,
          plan: subscription?.plan.slug ?? 'unknown',
        });
      }
      cleanUrl();
      void invalidateSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Plan identity, status (Active / Past Due), capacity, and lifecycle
  // recovery all live on the Plans page — Billing is purely how you pay
  // (details, saved card, invoices), each on its own card.
  return (
    <div className="space-y-4">
      {loading ? (
        <Card className="shrink-0">
          <CardContent>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading billing info...
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="shrink-0">
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Unable to load billing info.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void subscriptionQuery.refetch()}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : subscription ? (
        <>
          {/* A blocked org lands here constantly: every lifecycle email sent
              before AIT-444 pointed at /billing, the sidebar has a Billing
              item, and people bookmark it. Recovery itself lives on Plans
              (v2-plans-section's paused branch owns the setup-mode Checkout
              and the "Resume on" grid), so Billing's job is to hand them
              across rather than dead-end them. Wording avoids "your trial
              ended": a PAYING subscription can be paused from the Stripe
              dashboard, and telling that customer their trial ended is
              simply false (Codex, PR #218). */}
          {validOrgId &&
            (subscription.status === 'paused' || subscription.status === 'unpaid') && (
              <InfoAlert
                tone="amber"
                action={{ label: 'Choose A Plan', to: `/org/${validOrgId}/plans` }}
              >
                {subscription.status === 'paused'
                  ? 'Your channels stopped forwarding. Pick a plan to start them again.'
                  : 'Your previous plan ended, so your channels stopped forwarding. Pick a plan to start them again.'}
              </InfoAlert>
            )}
          {/* Native billing surface, rendered for both paid and free orgs
              (free orgs see empty shapes). Gated on a valid org public id.
              Details, saved card, and invoice history each get their own card.
              The next-payment date rides on the Billing History headline. */}
          {validOrgId && (
            <>
              {/* overflow-visible so the Billing address autocomplete dropdown
                  isn't clipped by the card's default overflow-hidden. */}
              <Card className="shrink-0 overflow-visible">
                <CardContent>
                  <BillingDetails orgId={validOrgId} />
                </CardContent>
              </Card>
              <Card className="shrink-0">
                <CardContent>
                  <BillingMethod orgId={validOrgId} />
                </CardContent>
              </Card>
              <Card className="shrink-0">
                <CardContent>
                  <BillingHistory
                    orgId={validOrgId}
                    nextPaymentDate={
                      hasActiveSub && !subscription.cancelAtPeriodEnd
                        ? subscription.currentPeriodEnd
                        : null
                    }
                  />
                </CardContent>
              </Card>
            </>
          )}
        </>
      ) : null}
    </div>
  );
}
